import React from 'react'
import { useTranslation } from 'react-i18next'
import { pageTypes } from 'common/constants/pageTypes'
import { PageTypeEnum } from 'common/enums/PageTypeEnum'
import useBlocks, { selectors as blocksSelectors } from 'client/hooks/useBlocks'
import usePresentPage, {
  selectors as pageSelectors,
} from 'client/hooks/usePresentPage'
import { blockCategories, blockCategoryNames } from './consts'
import { BlockCategoryType, BlockType } from './types'
import { twMerge } from 'tailwind-merge'

const blockCategoryIcons: Partial<
  Record<BlockCategoryType, React.JSX.Element>
> = {
  [blockCategories.optInForms]: <i className="fa-light fa-file-check" />,
  [blockCategories.features]: <i className="fa-light fa-thumbs-up" />,
  [blockCategories.teamMembers]: <i className="fa-light fa-users" />,
  [blockCategories.orderForms]: <i className="fa-light fa-cart-shopping" />,
  [blockCategories.hero]: <i className="fa-light fa-shopping-cart" />,
  [blockCategories.pricePlans]: <i className="fa-light fa-user" />,
  [blockCategories.pageHeaders]: <i className="fa-light fa-user" />,
  [blockCategories.pageFooters]: <i className="fa-light fa-down-to-line" />,
  [blockCategories.testimonials]: <i className="fa-light fa-message-text" />,
}

interface BlockCategoriesProps {
  pageType: PageTypeEnum
  setActiveCategory: (category: BlockCategoryType) => void
}

export const BlockCategories = ({
  pageType,
  setActiveCategory,
}: BlockCategoriesProps) => {
  const { t } = useTranslation()
  const commonBlocksByCategories: Record<BlockCategoryType, BlockType[]> =
    useBlocks(blocksSelectors.getCommonBlocksByCategories)

  const isOfferPageOrNotOrderFormCategory = (category: string) =>
    pageType === pageTypes.offerPage || category !== blockCategories.orderForms

  const isTemplate = usePresentPage(pageSelectors.isTemplate)
  const userBlocks: BlockType[] = useBlocks(blocksSelectors.getUserBlocks)
  const masterBlocks: BlockType[] = useBlocks(blocksSelectors.getMasterBlocks)
  const allowedCommonCategories = Object.keys(commonBlocksByCategories)
    .filter(isOfferPageOrNotOrderFormCategory)
    .map(category => ({
      name: t(blockCategoryNames[category as BlockCategoryType]),
      type: category as BlockCategoryType,
      icon: blockCategoryIcons[category as BlockCategoryType],
    }))

  return (
    <div>
      {!isTemplate && masterBlocks.length > 0 && (
        <BlockCategory
          label={t(blockCategoryNames.userMaster)}
          onClick={() => setActiveCategory(blockCategories.userMaster)}
          icon={<i className="fa-light fa-bolt" />}
        />
      )}
      {userBlocks.length > 0 && (
        <BlockCategory
          label={t(blockCategoryNames.userSimple)}
          onClick={() => setActiveCategory(blockCategories.userSimple)}
          icon={<i className="fa-light fa-floppy-disk" />}
        />
      )}
      {allowedCommonCategories.map(category => (
        <BlockCategory
          key={category.type}
          label={category.name}
          onClick={() => setActiveCategory(category.type)}
          icon={category.icon}
        />
      ))}
    </div>
  )
}

interface BlockCategoryProps {
  label: string
  onClick?: () => void
  icon?: React.ReactNode
}

function BlockCategory({ label, onClick, icon }: BlockCategoryProps) {
  return (
    <button
      type="button"
      className={twMerge(
        'flex w-full cursor-pointer items-center justify-start gap-2.5 hover:bg-btnSecondaryHover',
        'rounded-md px-3 py-4.5 text-subheading-2 text-textPrimary transition-[all_0.3s_cubic-bezier(0,0,0.8,1)]',
      )}
      onClick={onClick}
    >
      <span className="w-5 text-lg">{icon}</span>
      <div className="text-M">{label}</div>
    </button>
  )
}
